import { create } from 'zustand';
import { LeadService } from '../entities/Lead';
import type { LeadWithPhones } from '../entities/Lead';

export type ActiveLeadStatus = 'idle' | 'looking-up' | 'found' | 'not-found' | 'error';
export type ActiveLeadSource = 'auto' | 'manual' | null;

const RETRY_INTERVAL_MS = 30_000;

let loopCounter = 0;

interface ActiveLeadState {
  lead: LeadWithPhones | null;
  source: ActiveLeadSource;
  status: ActiveLeadStatus;
  lastError: string | null;
  lookupDurationMs: number | null;
  callId: string | null;
  phone: string | null;
  retryTimerId: ReturnType<typeof setTimeout> | null;
  loopId: number;

  lookupByPhone: (phone: string, callId?: string) => Promise<void>;
  beginRetryLoop: (phone: string, callId?: string) => void;
  stopRetryLoop: () => void;
  selectManually: (lead: LeadWithPhones) => void;
  clear: () => void;
}

export const useActiveLeadStore = create<ActiveLeadState>((set, get) => ({
  lead: null,
  source: null,
  status: 'idle',
  lastError: null,
  lookupDurationMs: null,
  callId: null,
  phone: null,
  retryTimerId: null,
  loopId: 0,

  lookupByPhone: async (phone, callId) => {
    console.log(`[ACTIVE-LEAD] 🔎 lookupByPhone start — phone=${phone}, callId=${callId ?? '(none)'}`);
    set({ status: 'looking-up', lastError: null, callId: callId ?? null });
    try {
      const r = await LeadService.lookupLive(phone, callId);
      console.log('[ACTIVE-LEAD] 📥 lookupLive response:', {
        found: r.found,
        source: r.source,
        duration_ms: r.duration_ms,
        leadId: r.lead?.id,
        leadName: r.lead?.name,
        externalId: r.lead?.external_id,
      });
      if (r.found && r.lead) {
        console.log(
          `%c[ACTIVE-LEAD] ✅ Lead matched (source=${r.source}) — ${r.lead.name} [${r.lead.id}]`,
          'color:#10b981;font-weight:bold;'
        );
        set({
          lead: r.lead,
          source: 'auto',
          status: 'found',
          lookupDurationMs: r.duration_ms,
        });
      } else {
        console.warn(
          `[ACTIVE-LEAD] ❌ No lead match for phone=${phone} (source=${r.source}, duration=${r.duration_ms}ms)`
        );
        set({
          lead: null,
          source: null,
          status: 'not-found',
          lookupDurationMs: r.duration_ms,
        });
      }
    } catch (e: unknown) {
      console.error('[ACTIVE-LEAD] 💥 lookupByPhone error:', e);
      set({
        status: 'error',
        lastError: e instanceof Error ? e.message : String(e),
      });
    }
  },

  beginRetryLoop: (phone, callId) => {
    // Cancel any prior loop (implicit reset on a new call).
    get().stopRetryLoop();
    const myLoopId = ++loopCounter;
    set({
      loopId: myLoopId,
      phone,
      callId: callId ?? null,
      lead: null,
      source: null,
      status: 'looking-up',
      lastError: null,
      lookupDurationMs: null,
    });

    // Note: 'error' status keeps retrying — transient backend failures are exactly
    // what the loop is designed to recover from. Stops are: found, stopRetryLoop,
    // or a newer beginRetryLoop (epoch check above).
    const attempt = () => {
      if (get().loopId !== myLoopId) return; // a newer loop replaced us
      get().lookupByPhone(phone, callId).finally(() => {
        const s = get();
        if (s.loopId !== myLoopId) return;     // newer loop replaced us mid-flight
        if (s.status === 'found') return;      // success → no further attempts
        const id = setTimeout(attempt, RETRY_INTERVAL_MS);
        set({ retryTimerId: id });
      });
    };
    attempt();
  },

  stopRetryLoop: () => {
    const { retryTimerId } = get();
    if (retryTimerId !== null) {
      clearTimeout(retryTimerId);
      set({ retryTimerId: null });
    }
  },

  selectManually: (lead) => {
    set({ lead, source: 'manual', status: 'found', lastError: null });
  },

  clear: () => {
    get().stopRetryLoop();
    set({
      lead: null,
      source: null,
      status: 'idle',
      lastError: null,
      lookupDurationMs: null,
      callId: null,
      phone: null,
      loopId: 0,
    });
  },
}));
